import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { getLoanStatuses } from 'src/pages/AgentLoansPage/AgentLoanStatusInfoMap';
import { loanTypeOptions } from 'src/pages/LoansPage/LoanTypesMap';
import {
    AgentLoansFilter,
    AgentLoansStore,
} from 'src/stores/AgentLoansStore/AgentLoansStore';
import { DateHelper } from 'src/utils/DateHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const AgentLoansHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <SearchPanel
            inputSearchFormName={formNames.getPath('filterSearch')}
            store={store}
            inputSearchPlaceholder={t(
                'Page.Agent.Loans.Filter.Search.Placeholder',
            )}
            renderPanel={(props) => <FilterPanel {...props} />}
        />
    );
});

export const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getLoanStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            onFinish={(values: FilterWithDateRange) => {
                store.pager.reset();
                store.filterCriteria?.applyFilter(
                    convertFormValuesToFilterCriteria(values),
                );
                store.loadList();
            }}
        >
            <StyledFormItem
                label={t('Page.Loans.Filter.Type')}
                name={formNames.getPath('filterLoanTypes')}
            >
                <Select allowClear mode="multiple">
                    {loanTypeOptions.map(({ id, title }) => (
                        <Select.Option value={id} key={id}>
                            {t(`data:loanType.${title}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledFormItem>
            <StyledFormItem
                label={t('Page.AgentLoans.Filter.Status')}
                name={formNames.getPath('filterStatuses')}
            >
                <Select allowClear mode="multiple">
                    {statuses.map((item) => (
                        <Select.Option value={item.id} key={item.id}>
                            {t(`data:status.loan.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledFormItem>
            <Form.Item
                label={t('Page.AgentLoans.Filter.IssuedAt')}
                name={formNames.getPath(dateRangeName)}
            >
                <DateRangePicker
                    placeholder={[
                        t('Component.DateRangePicker.Start'),
                        t('Component.DateRangePicker.End'),
                    ]}
                />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const store = AgentLoansStore;
const formNames = FormHelper.createNameProvider<FilterWithDateRange>();
const dateRangeName = 'filterIssueDate';

type FilterWithDateRange = Omit<
    AgentLoansFilter,
    'filterIssueDateFrom' | 'filterIssueDateTo'
> & {
    [dateRangeName]: [moment.Moment, moment.Moment];
};

const convertFormValuesToFilterCriteria = (
    values: FilterWithDateRange,
): AgentLoansFilter => {
    const { filterIssueDate, filterStatuses, filterLoanTypes, ...others } =
        values;
    const [filterIssueDateFrom, filterIssueDateTo] = filterIssueDate || [
        undefined,
        undefined,
    ];

    return {
        ...others,
        filterStatuses:
            filterStatuses && filterStatuses.length > 0
                ? filterStatuses
                : undefined,
        filterLoanTypes:
            filterLoanTypes && filterLoanTypes.length > 0
                ? filterLoanTypes
                : undefined,
        filterIssueDateFrom: filterIssueDateFrom
            ? DateHelper.formatToISO(filterIssueDateFrom.startOf('day'))
            : undefined,
        filterIssueDateTo: filterIssueDateTo
            ? DateHelper.formatToISO(filterIssueDateTo.endOf('day'))
            : undefined,
    };
};

const StyledFormItem = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: auto;
        border-radius: 0.5rem;
    }
`;
